import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAuth0 } from "@auth0/auth0-react";
import { PermitDocument, PermitDocumentReviewStatus } from "common/types/permits";
import { ImageViewerDialog } from "@app.automotus.io/components/ImageViewerDialog";
import { getDocumentUrl } from "common/helpers/documentUrl";

export type DocumentWithReviewStatus = Partial<Omit<PermitDocument, "id">> & {
  id: string;
  reviewStatus?: PermitDocumentReviewStatus;
};

export interface DocumentsListSectionProps {
  documents: DocumentWithReviewStatus[];
}

export const DocumentsListSection: React.FC<DocumentsListSectionProps> = ({ documents }) => {
  const [loadingState, setLoadingState] = useState<Record<string, "view" | "download" | false>>({});
  const [dialogState, setDialogState] = useState<{ url: string; filename: string } | null>(null);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const blobUrlsRef = useRef<string[]>([]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach((blobUrl) => {
        window.URL.revokeObjectURL(blobUrl);
      });
    };
  }, []);

  if (!documents || documents.length === 0) {
    return null;
  }

  const getViewerType = (contentType: string): "document" | "image" =>
    contentType.startsWith("image/") ? "image" : "document";

  const getViewerTypeFromFilename = (filename: string): "document" | "image" => {
    const ext = filename.split(".").pop()?.toLowerCase();
    return ext && ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext) ? "image" : "document";
  };

  const handleDocumentAction = async (doc: DocumentWithReviewStatus, action: "view" | "download") => {
    const url = getDocumentUrl(doc);
    if (!url || !doc.id) {
      return;
    }

    setLoadingState((prev) => ({ ...prev, [doc.id as string]: action }));

    try {
      const isInternalLink = url.includes("atmts.io");
      const filename = doc.fileName || "document.pdf";

      if (!isInternalLink) {
        if (action === "download") {
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          link.click();
        } else {
          if (getViewerTypeFromFilename(filename) === "image") {
            setDialogState({ url, filename });
          } else {
            window.open(url, "_blank");
          }
        }
        return;
      }

      // For internal links, fetch with authentication
      const token = isAuthenticated ? await getAccessTokenSilently() : undefined;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/pdf,application/octet-stream,*/*",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Track the blob URL for cleanup on component unmount
      blobUrlsRef.current.push(blobUrl);

      // Small delay to ensure the blob URL is fully processed before opening
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (action === "download") {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.click();
      } else {
        if (getViewerType(contentType) === "image") {
          setDialogState({ url: blobUrl, filename });
        } else {
          window.open(blobUrl, "_blank");
        }
      }
    } catch (err) {
      console.error(`Error ${action === "download" ? "downloading" : "opening"} document:`, err);
    } finally {
      setLoadingState((prev) => ({ ...prev, [doc.id as string]: false }));
    }
  };

  return (
    <Box>
      <ImageViewerDialog
        open={dialogState !== null}
        onClose={() => setDialogState(null)}
        url={dialogState?.url ?? ""}
        filename={dialogState?.filename}
      />
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Documentation ({documents.length})
      </Typography>
      <Stack spacing={1}>
        {documents.map((doc, index) => (
          <Paper key={doc.id || index} variant="outlined" sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, width: "100%" }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <InsertDriveFileIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={doc.documentType?.name || "Document"}
                  secondary={doc.fileName || "—"}
                  sx={{
                    "& .MuiListItemText-primary": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiListItemText-secondary": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Box>
              {doc.id && (
                <Stack direction="row" spacing={1} sx={{ ml: { xs: 0, sm: "auto" }, flexShrink: 0 }}>
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    onClick={() => handleDocumentAction(doc, "view")}
                    loading={loadingState[doc.id] === "view"}
                    startIcon={<VisibilityIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Preview
                  </LoadingButton>
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    onClick={() => handleDocumentAction(doc, "download")}
                    loading={loadingState[doc.id] === "download"}
                    startIcon={<DownloadIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Download
                  </LoadingButton>
                </Stack>
              )}
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default DocumentsListSection;
